import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Avatar from "@mui/material/Avatar";
import { shopApi, foldersApi } from "../api/shopApi";
import LoadingSpinner from "../components/LoadingSpinner";
import { handleErrors } from "../modules/handleErrors";
import WarningModal from "../modals/WarningModal";
import defaultShopBanner from "../assets/images/blog-img.png";

const PAGE_TITLE_STYLE = { color: "#000000e6" };
const SECTION_TITLE_CLASS = "fw-bold mb-3";
const SUBSECTION_TITLE_CLASS = "fw-bold mb-2";
const HELPER_TEXT_CLASS = "text-muted small mb-2";

const MyShop = () => {
  const { userid } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shop, setShop] = useState(null);
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [form, setForm] = useState({
    contact_email: "",
    facebook: "",
    instagram: "",
    pinterest: "",
    tiktok: "",
    about_title: "",
    about_text: "",
  });
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState("");
  const [aboutImageFile, setAboutImageFile] = useState(null);
  const [aboutImagePreview, setAboutImagePreview] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState("");
  const [showDeleteFolderWarning, setShowDeleteFolderWarning] = useState(false);
  const [folderToDeleteId, setFolderToDeleteId] = useState(null);
  const bannerInputRef = React.useRef(null);
  const aboutInputRef = React.useRef(null);

  const loadShop = async () => {
    if (!userid) return;
    setLoading(true);
    try {
      const data = await shopApi.getMine(userid);
      setShop(data);
      if (data) {
        setForm({
          contact_email: data.contact_email || "",
          facebook: data.facebook || data.contact_facebook || "",
          instagram: data.instagram || data.contact_instagram || "",
          pinterest: data.pinterest || data.contact_pinterest || "",
          tiktok: data.tiktok || data.contact_tiktok || "",
          about_title: data.about_title || "",
          about_text: data.about_text || "",
        });
        setBannerFile(null);
        setBannerPreview(data.banner || "");
        setAboutImageFile(null);
        setAboutImagePreview(data.about_image || "");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    if (!userid) return;
    setFoldersLoading(true);
    try {
      const list = await foldersApi.listMine(userid);
      setFolders(Array.isArray(list) ? list : []);
    } catch (e) {
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  };

  useEffect(() => {
    if(!userid) return;
    loadShop();
    loadFolders();
  }, [userid]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleBannerChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (bannerPreview && typeof bannerPreview === "string" && bannerPreview.startsWith("blob:")) {
        URL.revokeObjectURL(bannerPreview);
      }
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    }
  };

  const handleAboutImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (aboutImagePreview && typeof aboutImagePreview === "string" && aboutImagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(aboutImagePreview);
      }
      setAboutImageFile(file);
      setAboutImagePreview(URL.createObjectURL(file));
    }
  };

  const handleDeleteBannerSelection = () => {
    if (bannerPreview && typeof bannerPreview === "string" && bannerPreview.startsWith("blob:")) {
      URL.revokeObjectURL(bannerPreview);
    }
    setBannerFile(null);
    setBannerPreview("");
    if (bannerInputRef.current) bannerInputRef.current.value = "";
  };

  const handleDeleteAboutSelection = () => {
    if (aboutImagePreview && typeof aboutImagePreview === "string" && aboutImagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(aboutImagePreview);
    }
    setAboutImageFile(null);
    setAboutImagePreview("");
    if (aboutInputRef.current) aboutInputRef.current.value = "";
  };

  useEffect(() => {
    return () => {
      if (bannerPreview && typeof bannerPreview === "string" && bannerPreview.startsWith("blob:")) {
        URL.revokeObjectURL(bannerPreview);
      }
      if (aboutImagePreview && typeof aboutImagePreview === "string" && aboutImagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(aboutImagePreview);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveShop = async (e) => {
    e?.preventDefault();
    if (!userid) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("contact_email", form.contact_email);
      fd.append("facebook", form.facebook);
      fd.append("instagram", form.instagram);
      fd.append("pinterest", form.pinterest);
      fd.append("tiktok", form.tiktok);
      fd.append("about_title", form.about_title);
      fd.append("about_text", form.about_text);
      if (bannerFile) fd.append("banner", bannerFile);
      if (aboutImageFile) fd.append("about_image", aboutImageFile);

      if (shop?.id) {
        await shopApi.updateMine(fd , shop?.id);
        toast.success("Shop updated successfully");
      } else {
        await shopApi.createMine(fd);
        toast.success("Shop created successfully");
      }
      await loadShop();
    } catch (err) {
      const msg = err?.message || "Failed to save shop";
      toast.error(msg);
      if (err?.response?.data) handleErrors(err.response.data);
    } finally {
      setSaving(false);
    }
  };

  const handleAddFolder = async (e) => {
    e?.preventDefault();
    const name = (newFolderName || "").trim();
    if (!name) {
      toast.info("Enter a folder name");
      return;
    }
    try {
      const created = await foldersApi.create({ name });
      setFolders((prev) => [...prev, created].filter(Boolean));
      setNewFolderName("");
      toast.success("Folder created");
    } catch (err) {
      toast.error(err?.message || "Failed to create folder");
    }
  };

  const handleUpdateFolder = async (id) => {
    const name = (editingFolderName || "").trim();
    if (!name) return;
    try {
      await foldersApi.update(id, { name });
      setFolders((prev) => prev.map((f) => (f.id === id ? { ...f, name } : f)));
      setEditingFolderId(null);
      setEditingFolderName("");
      toast.success("Folder renamed");
    } catch (err) {
      toast.error(err?.message || "Failed to update folder");
    }
  };

  const handleDeleteFolder = async (id) => {
    try {
      await foldersApi.delete(id);
      setFolders((prev) => prev.filter((f) => f.id !== id));
      toast.success("Folder deleted");
    } catch (err) {
      toast.error(err?.message || "Failed to delete folder");
    }
  };

  const handleDeleteFolderConfirm = async () => {
    if (folderToDeleteId) {
      await handleDeleteFolder(folderToDeleteId);
      setFolderToDeleteId(null);
    }
    setShowDeleteFolderWarning(false);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <section className="py-4">
      <div className="container">
        <div className="row mb-3">
          <div className="col">
            <h2 className="mb-0" style={PAGE_TITLE_STYLE}>
              My Shop
            </h2>
            {/* <p className=" small mb-0">
              Customize your storefront. Buyers see this when they click your name.
            </p> */}
          </div>
        </div>

        <div
          className="p-md-4 mx-0 mx-md-3 mb-4"
          style={{ backgroundColor: "#F9FBFA", borderRadius: "24px" }}
        >
          <form onSubmit={handleSaveShop}>
            {/* Banner */}
            <div className="mb-4">
              <h5 className={SECTION_TITLE_CLASS} style={PAGE_TITLE_STYLE}>
                Shop banner
              </h5>
              <p className={HELPER_TEXT_CLASS}>
                Shown at the top of your store. Recommended size: 1200×400 px.
              </p>
              <div className="d-flex flex-column align-items-center gap-3 ">
                <div
                  className="rounded-5 overflow-hidden bg-white"
                  style={{
                    width: "100%",
                    aspectRatio: "1200/400",
                    border: "4px dashed #2F80ED",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    cursor: "pointer",
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={() => bannerInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") bannerInputRef.current?.click();
                  }}
                >
                  <img
                    src={bannerPreview || defaultShopBanner}
                    alt="Banner"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                      display: "block",
                    }}
                  />
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBannerChange}
                    id="shop-banner"
                    className="d-none"
                    ref={bannerInputRef}
                  />
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn grey-btn"
                      onClick={() => bannerInputRef.current?.click()}
                    >
                      {bannerPreview ? "Re-upload" : "Upload banner"}
                    </button>
                    {bannerPreview && (
                      <button
                        type="button"
                        className="btn red-btn"
                        onClick={handleDeleteBannerSelection}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="mb-4">
              <h5 className={SECTION_TITLE_CLASS} style={PAGE_TITLE_STYLE}>
                Contact
              </h5>
              <div className="mb-3">
                <label className="form-label small">Email</label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="contact@example.com"
                  value={form.contact_email}
                  onChange={(e) => handleChange("contact_email", e.target.value)}
                />
              </div>
              <h6 className={SUBSECTION_TITLE_CLASS} style={PAGE_TITLE_STYLE}>
                Socials
              </h6>
              <div className="row g-2">
                <div className="col-md-6">
                  <label className="form-label small">Facebook URL</label>
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://facebook.com/..."
                    value={form.facebook}
                    onChange={(e) => handleChange("facebook", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Instagram URL</label>
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://instagram.com/..."
                    value={form.instagram}
                    onChange={(e) => handleChange("instagram", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Pinterest URL</label>
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://pinterest.com/..."
                    value={form.pinterest}
                    onChange={(e) => handleChange("pinterest", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">TikTok URL</label>
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://tiktok.com/..."
                    value={form.tiktok}
                    onChange={(e) => handleChange("tiktok", e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* About (optional) */}
            <div className="mb-4">
              <h5 className={SECTION_TITLE_CLASS} style={PAGE_TITLE_STYLE}>
                About page
              </h5>
              <p className={HELPER_TEXT_CLASS}>
                Upload your photo and share a bit about yourself to help buyers get you know. Recommended size: 1500×900 px.
              </p>
              <div className="mb-2">
                <div className="d-flex align-items-center flex-column gap-3 flex-wrap">
                  <div
                    className="rounded-5 overflow-hidden bg-white"
                    style={{
                      width: 360,
                      maxWidth: "100%",
                      aspectRatio: "1500 / 900",
                      border: "4px dashed #2F80ED",
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      cursor: "pointer",
                    }}
                    role="button"
                    tabIndex={0}
                    onClick={() => aboutInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") aboutInputRef.current?.click();
                    }}
                  >
                    {aboutImagePreview ? (
                      <img
                        src={aboutImagePreview}
                        alt="About"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          objectPosition: "center",
                          padding: 8,
                          background: "#fff",
                        }}
                      />
                    ) : (
                      <div className="text-muted" style={{ fontSize: 14 }}>
                        Upload file
                      </div>
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAboutImageChange}
                      id="about-image"
                      className="d-none"
                      ref={aboutInputRef}
                    />
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn grey-btn"
                        onClick={() => aboutInputRef.current?.click()}
                      >
                        {aboutImagePreview ? "Re-upload" : "Upload image"}
                      </button>
                      {aboutImagePreview && (
                        <button
                          type="button"
                          className="btn red-btn"
                          onClick={handleDeleteAboutSelection}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-2">
                <label className="form-label small">Title</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="About me"
                  maxLength={30}
                  value={form.about_title}
                  onChange={(e) => handleChange("about_title", e.target.value)}
                />
                <small className="text-muted">{form.about_title.length}/30</small>
              </div>
              <div>
                <label className="form-label small">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  maxLength={2000}
                  placeholder="Tell buyers a bit about you or your shop..."
                  value={form.about_text}
                  onChange={(e) => handleChange("about_text", e.target.value)}
                />
                <small className="text-muted">{form.about_text.length}/2000</small>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2">
              <button type="submit" className="btn red-btn" disabled={saving}>
                {saving ? "Applying Changes…" : "Apply Changes"}
              </button>
              <a
                href={`/browse-mockups?name=${userid}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn grey-btn"
              >
                View my store
              </a>
            </div>
          </form>
        </div>

        {/* Folders */}
        <div
          className="p-md-4 mx-0 mx-md-3"
          style={{ backgroundColor: "#F9FBFA", borderRadius: "24px" }}
        >
          <h5 className={SECTION_TITLE_CLASS} style={PAGE_TITLE_STYLE}>
            Folders (categories in your store)
          </h5>
          <p className={`${HELPER_TEXT_CLASS} mb-3`}>
            Create folders and assign mockups to them in My Listings. They appear as categories on the left in your store.
          </p>
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <input
              type="text"
              className="form-control"
              style={{ maxWidth: 220 }}
              placeholder="New folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddFolder(e)}
            />
            <button type="button" className="btn red-btn" onClick={handleAddFolder}>
              Add folder
            </button>
          </div>
          <ul className="list-group list-group-flush">
            {foldersLoading ? (
              <li className="list-group-item d-flex justify-content-center py-4">
                <LoadingSpinner color="#C93312" />
              </li>
            ) : folders.length === 0 ? (
              <li className="list-group-item text-muted small">No folders yet. Add one above.</li>
            ) : null}
            {!foldersLoading && folders.map((f) => (
              <li
                key={f.id}
                className="list-group-item d-flex align-items-center justify-content-between flex-wrap gap-2"
              >
                {editingFolderId === f.id ? (
                  <>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ maxWidth: 200 }}
                      value={editingFolderName}
                      onChange={(e) => setEditingFolderName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleUpdateFolder(f.id);
                        if (e.key === "Escape") setEditingFolderId(null);
                      }}
                      autoFocus
                    />
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn red-btn"
                        onClick={() => handleUpdateFolder(f.id)}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn grey-btn"
                        onClick={() => setEditingFolderId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="fw-medium">{f.name}</span>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn grey-btn"
                        onClick={() => {
                          setEditingFolderId(f.id);
                          setEditingFolderName(f.name);
                        }}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="btn red-btn"
                        onClick={() => {
                          setFolderToDeleteId(f.id);
                          setShowDeleteFolderWarning(true);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <WarningModal
        showModal={showDeleteFolderWarning}
        modalClose={() => { setShowDeleteFolderWarning(false); setFolderToDeleteId(null); }}
        setConfirmClose={handleDeleteFolderConfirm}
        text="Delete this folder? Mockups in it will be unassigned."
      />
    </section>
  );
};

export default MyShop;
